import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Grid, Chip, Button, IconButton, TextField,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Tabs, Tab, Alert, Divider, InputAdornment, Tooltip, alpha,
  Card, CardContent, Popover, Checkbox, FormControlLabel, FormGroup
} from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import SearchIcon from '@mui/icons-material/Search';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import HistoryIcon from '@mui/icons-material/History';
import AttachFileIcon from '@mui/icons-material/AttachFile';

const fmt = v => Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCur = v => 'S/ ' + fmt(v);

const ESTADOS = ['PENDIENTE', 'PARCIAL', 'CANCELADO', 'ANULADO'];
const MEDIOS_PAGO = ['EFECTIVO', 'TRANSFERENCIA', 'TARJETA', 'CHEQUE', 'YAPE', 'PLIN', 'OTRO'];

const estadoColor = e => {
  if (e === 'CANCELADO') return 'success';
  if (e === 'PARCIAL') return 'warning';
  if (e === 'ANULADO') return 'default';
  return 'error';
};

const SectionHeader = ({ children, icon }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 0.5,
    borderLeft: '3px solid', borderColor: 'primary.main',
    pl: 1.5, py: 0.25, mb: 1.5, bgcolor: t => alpha(t.palette.primary.main, 0.06),
    borderRadius: '0 4px 4px 0'
  }}>
    {icon && <Box sx={{ color: 'primary.main', display: 'flex', mr: 0.5 }}>{icon}</Box>}
    <Typography variant="subtitle2" color="primary.dark" fontWeight={700}>{children}</Typography>
  </Box>
);

export default function Tesoreria() {
  const [tab, setTab] = useState(0); // 0=cobros, 1=pagos, 2=cuentas
  const [movimientos, setMovimientos] = useState([]);
  const [total, setTotal] = useState(0);
  const [uiPage, setUiPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [resumen, setResumen] = useState({});
  const [cuentas, setCuentas] = useState([]);

  const [filtros, setFiltros] = useState({ estados: [], desde: '', hasta: '', search: '' });
  const [estadoAnchor, setEstadoAnchor] = useState(null);

  // Cancel dialog
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelMov, setCancelMov] = useState(null);
  const [cancelForm, setCancelForm] = useState({ monto: '', medio_pago: 'EFECTIVO', cuenta_id: '', referencia: '', observaciones: '' });
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);
  const [cancelAdjuntos, setCancelAdjuntos] = useState([]);

  // Liberar dialog
  const [liberarDialog, setLiberarDialog] = useState(false);
  const [liberarMov, setLiberarMov] = useState(null);
  const [liberarLoading, setLiberarLoading] = useState(false);

  // Ver/Editar pagos dialog
  const [pagoDialog, setPagoDialog] = useState(false);
  const [pagoMov, setPagoMov] = useState(null);
  const [pagoCancelaciones, setPagoCancelaciones] = useState([]);
  const [pagoLoading, setPagoLoading] = useState(false);
  const [editCancelId, setEditCancelId] = useState(null);
  const [editCancelForm, setEditCancelForm] = useState({ fecha_cancelacion: '', medio_pago: 'EFECTIVO', cuenta_id: '', referencia: '', observaciones: '' });
  const [editCancelSaving, setEditCancelSaving] = useState(false);

  // Account dialog
  const [cuentaDialog, setCuentaDialog] = useState(false);
  const [cuentaEdit, setCuentaEdit] = useState(null);
  const [cuentaForm, setCuentaForm] = useState({ nombre: '', tipo: 'CAJA', moneda: 'PEN', saldo_inicial: 0, descripcion: '' });

  const tipo = tab === 0 ? 'COBRO' : tab === 1 ? 'PAGO' : '';

  const token = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  const cargarResumen = useCallback(async () => {
    try {
      const res = await axios.get('/api/tesoreria/resumen', { headers: headers() });
      setResumen(res.data || {});
    } catch { setResumen({}); }
  }, []);

  const cargarCuentas = useCallback(async () => {
    try {
      const res = await axios.get('/api/tesoreria/cuentas', { headers: headers() });
      setCuentas(res.data || []);
    } catch { setCuentas([]); }
  }, []);

  const cargarMovimientos = useCallback(async () => {
    if (tab === 2) return;
    setLoading(true);
    try {
      const params = { tipo, page: 1, limit: 500, search: filtros.search, desde: filtros.desde, hasta: filtros.hasta };
      if (filtros.estados && filtros.estados.length > 0) params.estado = filtros.estados.join(',');
      const res = await axios.get('/api/tesoreria', { params, headers: headers() });
      setMovimientos(res.data.rows || []);
      setTotal(res.data.total || 0);
      setUiPage(0);
    } catch { setMovimientos([]); }
    setLoading(false);
  }, [tab, tipo, filtros]);

  useEffect(() => { cargarResumen(); cargarCuentas(); }, [cargarResumen, cargarCuentas]);
  useEffect(() => { cargarMovimientos(); }, [cargarMovimientos]);

  const abrirCancelar = (mov) => {
    setCancelMov(mov);
    setCancelForm({ monto: fmt(mov.monto_pendiente).replace(/,/g, ''), medio_pago: 'EFECTIVO', cuenta_id: cuentas[0]?.id || '', referencia: '', observaciones: '' });
    setCancelResult(null);
    setCancelAdjuntos([]);
    setCancelDialog(true);
  };

  const ejecutarCancelar = async () => {
    setCancelLoading(true);
    setCancelResult(null);
    try {
      let adjuntos_json = null;
      if (cancelAdjuntos.length > 0) {
        const fd = new FormData();
        fd.append('movimiento_id', cancelMov.id);
        cancelAdjuntos.forEach(f => fd.append('adjuntos', f));
        const upRes = await axios.post('/api/tesoreria/upload-adjuntos', fd, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        adjuntos_json = JSON.stringify(upRes.data.files || []);
      }
      const res = await axios.post(`/api/tesoreria/${cancelMov.id}/cancelar`, { ...cancelForm, adjuntos_json }, { headers: headers() });
      setCancelResult({ tipo: 'success', data: res.data });
      await cargarMovimientos();
      await cargarResumen();
    } catch (err) {
      setCancelResult({ tipo: 'error', msg: err.response?.data?.error || err.message });
    }
    setCancelLoading(false);
  };

  const ejecutarAnular = async (id) => {
    if (!window.confirm('¿Anular este movimiento? Solo usar cuando el documento fue anulado en el sistema.')) return;
    try {
      await axios.post(`/api/tesoreria/${id}/anular`, {}, { headers: headers() });
      await cargarMovimientos(); await cargarResumen();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const abrirLiberar = (mov) => {
    setLiberarMov(mov);
    setLiberarDialog(true);
  };

  const ejecutarLiberar = async () => {
    setLiberarLoading(true);
    try {
      await axios.post(`/api/tesoreria/${liberarMov.id}/liberar`, {}, { headers: headers() });
      setLiberarDialog(false);
      await cargarMovimientos();
      await cargarResumen();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
    setLiberarLoading(false);
  };

  const abrirPagos = async (mov) => {
    setPagoMov(mov);
    setPagoDialog(true);
    setPagoLoading(true);
    setEditCancelId(null);
    try {
      const res = await axios.get(`/api/tesoreria/${mov.id}`, { headers: headers() });
      setPagoCancelaciones(res.data.cancelaciones || []);
    } catch { setPagoCancelaciones([]); }
    setPagoLoading(false);
  };

  const abrirEditCancel = (c) => {
    setEditCancelId(c.id);
    setEditCancelForm({
      fecha_cancelacion: c.fecha_cancelacion ? String(c.fecha_cancelacion).slice(0, 10) : '',
      medio_pago: c.medio_pago || 'EFECTIVO',
      cuenta_id: c.cuenta_id || '',
      referencia: c.referencia || '',
      observaciones: c.observaciones || ''
    });
  };

  const guardarEditCancel = async () => {
    setEditCancelSaving(true);
    try {
      await axios.put(`/api/tesoreria/${pagoMov.id}/cancelaciones/${editCancelId}`, editCancelForm, { headers: headers() });
      setEditCancelId(null);
      const res = await axios.get(`/api/tesoreria/${pagoMov.id}`, { headers: headers() });
      setPagoCancelaciones(res.data.cancelaciones || []);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
    setEditCancelSaving(false);
  };

  const handleExportExcel = async () => {
    try {
      const { exportTesoreriaWorkbook } = await import('../utils/excel/tesoreriaWorkbook');
      const tipoStr = tab === 0 ? 'COBRO' : tab === 1 ? 'PAGO' : '';
      await exportTesoreriaWorkbook({
        movimientos,
        tipo: tipoStr,
        filtros: { ...filtros, estado: filtros.estados.join(', ') || 'Todos' },
        usuario: localStorage.getItem('usuario') || ''
      });
    } catch (err) {
      alert('Error al exportar Excel: ' + err.message);
    }
  };

  const abrirCuenta = (c = null) => {
    setCuentaEdit(c);
    setCuentaForm(c ? { nombre: c.nombre, tipo: c.tipo, moneda: c.moneda, saldo_inicial: c.saldo_inicial, descripcion: c.descripcion || '' }
      : { nombre: '', tipo: 'CAJA', moneda: 'PEN', saldo_inicial: 0, descripcion: '' });
    setCuentaDialog(true);
  };

  const guardarCuenta = async () => {
    try {
      if (cuentaEdit) {
        await axios.put(`/api/tesoreria/cuentas/${cuentaEdit.id}`, cuentaForm, { headers: headers() });
      } else {
        await axios.post('/api/tesoreria/cuentas', cuentaForm, { headers: headers() });
      }
      setCuentaDialog(false);
      await cargarCuentas();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const montoIngresado = Number(cancelForm.monto) || 0;
  const montoPendiente = Number(cancelMov?.monto_pendiente) || 0;
  const vuelto = montoIngresado > montoPendiente ? montoIngresado - montoPendiente : 0;

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      {/* Título */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AccountBalanceWalletIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={800} color="primary.dark">Tesorería</Typography>
          <Typography variant="body2" color="text.secondary">Gestión de cobros, pagos y cuentas</Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <IconButton onClick={() => { cargarResumen(); cargarMovimientos(); }} title="Actualizar">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Resumen cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderTop: '3px solid', borderColor: 'success.main' }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="success" />
                <Typography variant="body2" color="text.secondary">Cobros Pendientes</Typography>
              </Box>
              <Typography variant="h6" fontWeight={700} color="success.dark">{fmtCur(resumen.cobros_pendientes)}</Typography>
              <Typography variant="caption" color="text.secondary">{resumen.cobros_pendientes_count || 0} documentos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderTop: '3px solid', borderColor: 'error.main' }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDownIcon color="error" />
                <Typography variant="body2" color="text.secondary">Pagos Pendientes</Typography>
              </Box>
              <Typography variant="h6" fontWeight={700} color="error.dark">{fmtCur(resumen.pagos_pendientes)}</Typography>
              <Typography variant="caption" color="text.secondary">{resumen.pagos_pendientes_count || 0} documentos</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderTop: '3px solid', borderColor: 'info.main' }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="info" />
                <Typography variant="body2" color="text.secondary">Cobrado hoy</Typography>
              </Box>
              <Typography variant="h6" fontWeight={700} color="info.dark">{fmtCur(resumen.cobros_cobrados_hoy)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderTop: '3px solid', borderColor: 'warning.main' }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoneyIcon color="warning" />
                <Typography variant="body2" color="text.secondary">Balance (Cob-Pag pendiente)</Typography>
              </Box>
              <Typography variant="h6" fontWeight={700} color={Number(resumen.cobros_pendientes) - Number(resumen.pagos_pendientes) >= 0 ? 'success.dark' : 'error.dark'}>
                {fmtCur(Number(resumen.cobros_pendientes || 0) - Number(resumen.pagos_pendientes || 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Cobros (Ventas)" icon={<TrendingUpIcon />} iconPosition="start" />
          <Tab label="Pagos (Compras)" icon={<TrendingDownIcon />} iconPosition="start" />
          <Tab label="Cuentas" icon={<AccountBalanceIcon />} iconPosition="start" />
        </Tabs>

        {tab < 2 && (
          <Box sx={{ p: 2 }}>
            {/* Filtros */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Buscar" size="small" sx={{ flex: '2 1 200px' }}
                value={filtros.search}
                onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <>
                <Button
                  variant="outlined" size="small"
                  onClick={e => setEstadoAnchor(e.currentTarget)}
                  sx={{ minWidth: 140, justifyContent: 'space-between', textTransform: 'none', borderColor: 'divider', color: 'text.primary' }}
                  endIcon={<span style={{fontSize:'0.7rem'}}>▼</span>}
                >
                  {filtros.estados.length === 0 ? 'Estado: Todos' : `Estado: ${filtros.estados.length} sel.`}
                </Button>
                <Popover
                  open={Boolean(estadoAnchor)} anchorEl={estadoAnchor}
                  onClose={() => setEstadoAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box sx={{ p: 1.5, minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>ESTADO</Typography>
                    <FormGroup>
                      {ESTADOS.map(e => (
                        <FormControlLabel key={e} control={
                          <Checkbox size="small"
                            checked={filtros.estados.includes(e)}
                            onChange={ev => setFiltros(f => ({
                              ...f, estados: ev.target.checked ? [...f.estados, e] : f.estados.filter(x => x !== e)
                            }))}
                          />
                        } label={<Typography variant="body2">{e}</Typography>} />
                      ))}
                    </FormGroup>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button size="small" onClick={() => setFiltros(f => ({ ...f, estados: [] }))}>Limpiar</Button>
                      <Button size="small" variant="contained" onClick={() => setEstadoAnchor(null)}>OK</Button>
                    </Box>
                  </Box>
                </Popover>
              </>
              <TextField type="date" label="Desde" size="small" sx={{ minWidth: 145 }}
                value={filtros.desde} onChange={e => setFiltros(f => ({ ...f, desde: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
              <TextField type="date" label="Hasta" size="small" sx={{ minWidth: 145 }}
                value={filtros.hasta} onChange={e => setFiltros(f => ({ ...f, hasta: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
              <Button variant="contained" size="small" onClick={cargarMovimientos}>Buscar</Button>
              <Button variant="outlined" size="small" onClick={() => setFiltros({ estados: [], desde: '', hasta: '', search: '' })}>Limpiar</Button>
              <Button
                variant="contained" size="small" color="success"
                startIcon={<GridOnIcon />}
                onClick={handleExportExcel}
                disabled={movimientos.length === 0}
              >
                Excel
              </Button>
            </Box>

            {/* Tabla */}
            {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      {['#', 'Documento', 'Auxiliar', 'Condición', 'Moneda', 'Total', 'Cancelado', 'Pendiente', 'F.Documento', 'Estado', 'Acciones'].map(h => (
                        <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movimientos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No hay registros
                        </TableCell>
                      </TableRow>
                    ) : movimientos.slice(uiPage * rowsPerPage, (uiPage + 1) * rowsPerPage).map((m, i) => (
                      <TableRow key={m.id} hover sx={{ '&:nth-of-type(odd)': { bgcolor: t => alpha(t.palette.primary.main, 0.03) } }}>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{uiPage * rowsPerPage + i + 1}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          <Typography variant="caption" fontWeight={600}>{m.tipo_documento}</Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">{m.serie}-{m.numero}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 160, overflow: 'hidden' }}>
                          <Tooltip title={m.auxiliar_nombre || ''} placement="top" arrow>
                            <Typography variant="caption" noWrap sx={{ display: 'block' }}>{m.auxiliar_nombre || '—'}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{m.condicion_pago || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{m.moneda}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }} align="right">{fmt(m.monto_total)}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'success.dark' }} align="right">{fmt(m.monto_cancelado)}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: m.monto_pendiente > 0 ? 'error.dark' : 'text.secondary', fontWeight: m.monto_pendiente > 0 ? 700 : 400 }} align="right">
                          {fmt(m.monto_pendiente)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{m.fecha_documento ? String(m.fecha_documento).slice(0, 10) : '—'}</TableCell>
                        <TableCell>
                          <Chip label={m.estado} color={estadoColor(m.estado)} size="small" sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {['PENDIENTE', 'PARCIAL'].includes(m.estado) && (
                              <Tooltip title="Registrar pago/cobro">
                                <IconButton size="small" color="success" onClick={() => abrirCancelar(m)}>
                                  <PaymentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {['CANCELADO', 'PARCIAL', 'ANULADO'].includes(m.estado) && (
                              <Tooltip title="Ver pagos registrados">
                                <IconButton size="small" color="primary" onClick={() => abrirPagos(m)}>
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {m.has_adjuntos > 0 && (
                              <Tooltip title="Tiene documentos adjuntos">
                                <IconButton size="small" color="info" onClick={() => abrirPagos(m)}>
                                  <AttachFileIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {['CANCELADO', 'PARCIAL', 'ANULADO'].includes(m.estado) && (
                              <Tooltip title="Liberar pagos (volver a Pendiente)">
                                <IconButton size="small" color="warning" onClick={() => abrirLiberar(m)}>
                                  <LockOpenIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {m.estado === 'PENDIENTE' && (
                              <Tooltip title="Anular movimiento">
                                <IconButton size="small" color="error" onClick={() => ejecutarAnular(m.id)}>
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {movimientos.length === 0
                  ? 'Sin registros'
                  : `Mostrando ${uiPage * rowsPerPage + 1}–${Math.min((uiPage + 1) * rowsPerPage, movimientos.length)} de ${movimientos.length} registros`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField select size="small" value={rowsPerPage}
                  onChange={e => { setRowsPerPage(Number(e.target.value)); setUiPage(0); }}
                  sx={{ minWidth: 105 }}>
                  {[8, 15, 25, 50].map(n => <MenuItem key={n} value={n}>{n} / pág</MenuItem>)}
                </TextField>
                <Button size="small" variant="outlined" disabled={uiPage === 0} onClick={() => setUiPage(p => p - 1)}>Anterior</Button>
                <Typography variant="caption" sx={{ px: 0.5 }}>
                  {uiPage + 1} / {Math.max(1, Math.ceil(movimientos.length / rowsPerPage))}
                </Typography>
                <Button size="small" variant="outlined" disabled={(uiPage + 1) * rowsPerPage >= movimientos.length} onClick={() => setUiPage(p => p + 1)}>Siguiente</Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* CUENTAS tab */}
        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <SectionHeader icon={<AccountBalanceIcon />}>Cuentas de Tesorería</SectionHeader>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => abrirCuenta()}>
                Nueva Cuenta
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    {['Nombre', 'Tipo', 'Moneda', 'Saldo Inicial', 'Estado', ''].map(h => (
                      <TableCell key={h} sx={{ color: 'white', fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cuentas.map(c => (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.nombre}</TableCell>
                      <TableCell><Chip label={c.tipo} size="small" variant="outlined" /></TableCell>
                      <TableCell>{c.moneda}</TableCell>
                      <TableCell align="right">{fmt(c.saldo_inicial)}</TableCell>
                      <TableCell><Chip label={c.estado ? 'Activo' : 'Inactivo'} color={c.estado ? 'success' : 'default'} size="small" /></TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => abrirCuenta(c)}><EditIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* ── DIALOG CANCELACIÓN ─────────────────────────────────────────────────── */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon />
          Registrar {cancelMov?.tipo === 'COBRO' ? 'Cobro' : 'Pago'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {cancelMov && (
            <>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: t => alpha(t.palette.primary.main, 0.04) }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Documento</Typography>
                    <Typography variant="body2" fontWeight={600}>{cancelMov.tipo_documento} {cancelMov.serie}-{cancelMov.numero}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">{cancelMov.tipo === 'COBRO' ? 'Cliente' : 'Proveedor'}</Typography>
                    <Typography variant="body2" fontWeight={600}>{cancelMov.auxiliar_nombre || '—'}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography variant="body2">{fmtCur(cancelMov.monto_total)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Cancelado</Typography>
                    <Typography variant="body2" color="success.dark">{fmtCur(cancelMov.monto_cancelado)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Pendiente</Typography>
                    <Typography variant="body2" color="error.dark" fontWeight={700}>{fmtCur(cancelMov.monto_pendiente)}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Monto a cancelar" size="small" type="number"
                    value={cancelForm.monto}
                    onChange={e => setCancelForm(f => ({ ...f, monto: e.target.value }))}
                    inputProps={{ step: '0.01', min: '0' }}
                    InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth label="Medio de pago" size="small"
                    value={cancelForm.medio_pago}
                    onChange={e => setCancelForm(f => ({ ...f, medio_pago: e.target.value }))}
                  >
                    {MEDIOS_PAGO.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth label="Cuenta" size="small"
                    value={cancelForm.cuenta_id}
                    onChange={e => setCancelForm(f => ({ ...f, cuenta_id: e.target.value }))}
                  >
                    <MenuItem value="">Sin cuenta</MenuItem>
                    {cuentas.map(c => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Referencia" size="small" placeholder="Nro operación"
                    value={cancelForm.referencia}
                    onChange={e => setCancelForm(f => ({ ...f, referencia: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Observaciones" size="small" multiline rows={2}
                    value={cancelForm.observaciones}
                    onChange={e => setCancelForm(f => ({ ...f, observaciones: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="outlined" size="small" component="label" startIcon={<AttachFileIcon />}>
                      Adjuntar archivos
                      <input type="file" hidden multiple onChange={e => setCancelAdjuntos(prev => [...prev, ...Array.from(e.target.files)])} />
                    </Button>
                    {cancelAdjuntos.length > 0 && (
                      <Typography variant="caption" color="text.secondary">{cancelAdjuntos.length} archivo(s) seleccionado(s)</Typography>
                    )}
                  </Box>
                  {cancelAdjuntos.map((f, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <AttachFileIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ flex: 1 }}>{f.name}</Typography>
                      <IconButton size="small" onClick={() => setCancelAdjuntos(prev => prev.filter((_, j) => j !== i))}>
                        <span style={{ fontSize: '0.8rem', lineHeight: 1 }}>✕</span>
                      </IconButton>
                    </Box>
                  ))}
                </Grid>
              </Grid>

              {/* Vuelto */}
              {vuelto > 0 && (
                <Alert severity="info" sx={{ mt: 2 }} icon={<AttachMoneyIcon />}>
                  <strong>Vuelto a entregar: {fmtCur(vuelto)}</strong>
                  <br />
                  <Typography variant="caption">El monto ingresado excede el pendiente. Se cancelará {fmtCur(montoPendiente)} y el vuelto es {fmtCur(vuelto)}.</Typography>
                </Alert>
              )}

              {cancelResult && (
                <Alert severity={cancelResult.tipo === 'success' ? 'success' : 'error'} sx={{ mt: 2 }}>
                  {cancelResult.tipo === 'success'
                    ? `Cancelado correctamente. Nuevo estado: ${cancelResult.data?.estado}. Pendiente: ${fmtCur(cancelResult.data?.monto_pendiente)}`
                    : cancelResult.msg}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCancelDialog(false)} variant="outlined">Cerrar</Button>
          {cancelResult?.tipo !== 'success' && (
            <Button
              variant="contained" color="success" startIcon={cancelLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              disabled={cancelLoading || !cancelForm.monto || Number(cancelForm.monto) <= 0}
              onClick={ejecutarCancelar}
            >
              Confirmar {cancelMov?.tipo === 'COBRO' ? 'Cobro' : 'Pago'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── DIALOG LIBERAR ─────────────────────────────────────────────────────── */}
      <Dialog open={liberarDialog} onClose={() => setLiberarDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockOpenIcon />
          Liberar pagos
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {liberarMov && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Se eliminarán todos los pagos registrados y el documento volverá a estado <strong>PENDIENTE</strong>.
              </Alert>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: t => alpha(t.palette.warning.main, 0.05) }}>
                <Typography variant="body2" fontWeight={600}>{liberarMov.tipo_documento} {liberarMov.serie}-{liberarMov.numero}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{liberarMov.auxiliar_nombre || '—'}</Typography>
                <Typography variant="body2">Total: <strong>{fmtCur(liberarMov.monto_total)}</strong> · Cancelado: <strong>{fmtCur(liberarMov.monto_cancelado)}</strong></Typography>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLiberarDialog(false)} variant="outlined">Cancelar</Button>
          <Button
            variant="contained" color="warning"
            startIcon={liberarLoading ? <CircularProgress size={16} /> : <LockOpenIcon />}
            disabled={liberarLoading}
            onClick={ejecutarLiberar}
          >
            Confirmar liberación
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG VER/EDITAR PAGOS ─────────────────────────────────────────────── */}
      <Dialog open={pagoDialog} onClose={() => { setPagoDialog(false); setEditCancelId(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          Pagos registrados — {pagoMov?.tipo_documento} {pagoMov?.serie}-{pagoMov?.numero}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {pagoLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>
          ) : pagoCancelaciones.length === 0 ? (
            <Alert severity="info">No hay pagos registrados para este movimiento.</Alert>
          ) : (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    {['Fecha', 'Monto', 'Medio de pago', 'Cuenta', 'Referencia', 'Observaciones', 'Adjuntos', ''].map(h => (
                      <TableCell key={h} sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagoCancelaciones.map(c => (
                    editCancelId === c.id ? (
                      <TableRow key={c.id} sx={{ bgcolor: t => alpha(t.palette.warning.main, 0.08) }}>
                        <TableCell sx={{ minWidth: 130 }}>
                          <TextField type="date" size="small" fullWidth
                            value={editCancelForm.fecha_cancelacion}
                            onChange={e => setEditCancelForm(f => ({ ...f, fecha_cancelacion: e.target.value }))}
                            InputLabelProps={{ shrink: true }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{fmtCur(c.monto)}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <TextField select size="small" fullWidth
                            value={editCancelForm.medio_pago}
                            onChange={e => setEditCancelForm(f => ({ ...f, medio_pago: e.target.value }))}>
                            {MEDIOS_PAGO.map(mp => <MenuItem key={mp} value={mp}>{mp}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <TextField select size="small" fullWidth
                            value={editCancelForm.cuenta_id}
                            onChange={e => setEditCancelForm(f => ({ ...f, cuenta_id: e.target.value }))}>
                            <MenuItem value="">Sin cuenta</MenuItem>
                            {cuentas.map(ct => <MenuItem key={ct.id} value={ct.id}>{ct.nombre}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          <TextField size="small" fullWidth value={editCancelForm.referencia}
                            onChange={e => setEditCancelForm(f => ({ ...f, referencia: e.target.value }))} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <TextField size="small" fullWidth value={editCancelForm.observaciones}
                            onChange={e => setEditCancelForm(f => ({ ...f, observaciones: e.target.value }))} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button size="small" variant="contained" color="warning" disabled={editCancelSaving} onClick={guardarEditCancel}>
                              {editCancelSaving ? <CircularProgress size={14} /> : 'Guardar'}
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => setEditCancelId(null)}>✕</Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={c.id} hover>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{c.fecha_cancelacion ? String(c.fecha_cancelacion).slice(0, 10) : '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }} align="right">{fmtCur(c.monto)}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{c.medio_pago}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{c.cuenta_nombre || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{c.referencia || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{c.observaciones || '—'}</TableCell>
                        <TableCell>
                          {c.adjuntos_json && (() => {
                            try {
                              const files = JSON.parse(c.adjuntos_json);
                              return files.map((fname, j) => (
                                <Box key={j}
                                  onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = `/api/tesoreria/adjunto/${pagoMov.id}/${encodeURIComponent(fname)}?token=${localStorage.getItem('token') || ''}`;
                                    link.download = fname;
                                    link.click();
                                  }}
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: 'primary.main', cursor: 'pointer' }}
                                >
                                  <AttachFileIcon sx={{ fontSize: '0.8rem' }} />
                                  <Typography variant="caption" sx={{ textDecoration: 'underline' }}>{fname}</Typography>
                                </Box>
                              ));
                            } catch { return '—'; }
                          })()}
                          {!c.adjuntos_json && <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => abrirEditCancel(c)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setPagoDialog(false); setEditCancelId(null); }} variant="outlined">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* ── DIALOG CUENTA ──────────────────────────────────────────────────────── */}
      <Dialog open={cuentaDialog} onClose={() => setCuentaDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          {cuentaEdit ? 'Editar Cuenta' : 'Nueva Cuenta'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nombre" size="small" value={cuentaForm.nombre}
                onChange={e => setCuentaForm(f => ({ ...f, nombre: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Tipo" size="small" value={cuentaForm.tipo}
                onChange={e => setCuentaForm(f => ({ ...f, tipo: e.target.value }))}>
                {['CAJA', 'BANCO', 'TARJETA', 'OTRO'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Moneda" size="small" value={cuentaForm.moneda}
                onChange={e => setCuentaForm(f => ({ ...f, moneda: e.target.value }))}>
                <MenuItem value="PEN">PEN (Soles)</MenuItem>
                <MenuItem value="USD">USD (Dólares)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Saldo inicial" size="small" type="number"
                value={cuentaForm.saldo_inicial}
                onChange={e => setCuentaForm(f => ({ ...f, saldo_inicial: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">S/</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" size="small" value={cuentaForm.descripcion}
                onChange={e => setCuentaForm(f => ({ ...f, descripcion: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCuentaDialog(false)} variant="outlined">Cancelar</Button>
          <Button onClick={guardarCuenta} variant="contained" disabled={!cuentaForm.nombre}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
